import { api } from '@organization/api'
import { useSuccessLogin } from '@organization/hooks'
import { MFAResponseSendOTP, WithCustomMFAToken } from '@organization/models'
import { paths } from '@organization/router'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Button, Card, Form, Input } from 'antd'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { AuthWrapper, OrganizationLogo } from '../components'
import { useStyle } from '../components/AuthWrapper/styles'

type LocationState = MFAResponseSendOTP &
  WithCustomMFAToken & { __created_at?: Date }

export const ConfirmMFABackUpCode = () => {
  const { styles } = useStyle()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { auth } = useSuccessLogin()

  const locationState = useMemo(
    () => location.state,
    [location]
  ) as LocationState

  const checkErrorCodes = (
    error: AxiosError<{ code: string; detail: string }>
  ) => {
    const code = error.response?.data?.code
    const detail = error.response?.data?.detail

    if (code === 'token_not_valid') {
      showApiErrors({ body: t('error.token_not_valid') })
      navigate(paths.auth.login, { replace: true })
      return
    } else if (code?.includes('backup_code') && detail) {
      showApiErrors({ response: { data: { backup_code: detail } } }, form)
      return
    }

    showApiErrors(error, form)
  }

  const { mutate: verifyBackup, isPending: isVerifyBackupPending } =
    useMutation({
      mutationFn: api.mfa.verifyBackupCode,
      onSuccess: auth,
      onError: checkErrorCodes
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    verifyBackup({
      ...values,
      __mfaAccessToken: locationState.__mfaAccessToken
    })
  }

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <OrganizationLogo
          message={(name) =>
            `${t('auth.sign_in_header')} ${name} ${t('common.account')}`
          }
        />

        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          requiredMark={false}
        >
          <Form.Item
            name="backup_code"
            label={t('form.item.backup_code')}
            rules={[{ required: true, message: t('form.message.backup_code') }]}
          >
            <Input.OTP
              length={8}
              style={{ justifyContent: 'center', width: '100%' }}
              size="large"
              formatter={(str) => str.toUpperCase()}
            />
          </Form.Item>

          <Button
            block
            type="primary"
            htmlType="submit"
            loading={isVerifyBackupPending}
            onClick={onSubmit}
          >
            {t('button.sign_in')}
          </Button>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
